import {
  createFileRoute,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import Button from 'components/Button/Button';
import useAppLanguage from 'hooks/useAppLanguage';
import usePageConfig from 'hooks/usePageConfig';
import React, { useState } from 'react';
import {
  useActivateProfileMutation,
  useGetProfileQuery,
} from 'store/slices/profile-api';
import { useGetUserTermsQuery } from 'store/slices/termsAndConditions-api';
import { EVENTS, SIGNUP_STEPS, trackEvent } from 'utils/analytics';
import { getMarketingUrl } from 'utils/app-urls';
import { isIFACode } from 'utils/promo-code';
import messages from '../../messages';
import { AcceptTermsAndConditionsState } from '../types/AcceptTermsAndConditionsState';
import { useRefreshTokenMutation } from 'app/auth/api/auth-api';

export const Route = createFileRoute('/auth/signup/_layout/tos')({
  component: AcceptTermsAndConditions,
});

function AcceptTermsAndConditions() {
  const navigate = useNavigate();
  const router = useRouter();

  const pageConfig = usePageConfig();
  const legalDocumentsUrl = `${getMarketingUrl()}/legal/terms-of-service`;
  const supportedCurrenciesUrl = `${getMarketingUrl()}/help-centre`;

  const language = useAppLanguage();
  const acceptTermsAndConditionsMessages =
    messages[language].acceptTermsAndConditions;

  const { data: userProfileResponse } = useGetProfileQuery();
  const { data: acceptedTermsList } = useGetUserTermsQuery();
  const [acceptTerms, acceptTermsResult] = useActivateProfileMutation();
  const [refreshToken] = useRefreshTokenMutation();
  const customerPromoCode = userProfileResponse?.promoCode?.toUpperCase();
  const isSaCustomer = userProfileResponse?.countryCode == 'ZA';
  const rebalanceTermIsActive =
    acceptedTermsList?.find(t => t.code == 'BUNDLE_REBALANCE') ?? false;
  const showBundleRebalanceCheckbox = isSaCustomer && rebalanceTermIsActive;

  const isIFACustomer = isIFACode(customerPromoCode);

  const [state, setState] = useState<AcceptTermsAndConditionsState>({});

  function isStateValid(): boolean {
    if (!state.acceptedTsAndCs) return false;
    if (!state.taxLiabilityNotInRestrictedCountry) return false;
    if (!state.supportedCurrencies) return false;
    if (isIFACustomer) {
      if (!state.hasIFAAccount) return false;
    }
    return true;
  }

  async function onContinuePress() {
    const result = await acceptTerms({
      revixRoundUp: state.acceptNewsletter || false,
      acceptedTerms: state.bundleRebalance ? ['BUNDLE_REBALANCE'] : [],
    });
    if (!result.error) {
      await refreshToken();
      trackEvent(EVENTS.REGISTER, {
        step: SIGNUP_STEPS.TERMS,
        userId: userProfileResponse?.id ?? undefined,
      });
      navigate({ to: '/auth/signup/welcome' });
    }
  }

  return (
    <>
      <div className="bg-ghost-white m-4">
        <div className="sm:max-w-lg sm:mx-auto sm:w-full">
          <h2 className="font-semibold leading-9 m-6 ml-0 text-1.46x1 text-black">
            {acceptTermsAndConditionsMessages.heading}
          </h2>

          <div className="flex items-start py-4">
            <div className="flex items-center flex-shrink-0">
              <input
                data-browsing-ignore
                id="acceptedTsAndCs"
                type="checkbox"
                className="form-checkbox h-6 w-6 text-indigo-600 transition duration-150 ease-in-out rounded-md"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setState(prevState => ({
                    ...prevState,
                    acceptedTsAndCs: event.target.checked,
                  }));
                }}
              />
            </div>
            <div className="ml-4 text-sm leading-5">
              <label
                htmlFor="acceptedTsAndCs"
                className="text-gray-700 font-light">
                {acceptTermsAndConditionsMessages.acceptedTsAndCsLabel}&nbsp;
                <a
                  href={legalDocumentsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline">
                  {acceptTermsAndConditionsMessages.termsLink}
                </a>
                ,&nbsp;
                <a
                  href={legalDocumentsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline">
                  {acceptTermsAndConditionsMessages.privacyPolicyLink}
                </a>
                ,&nbsp;
                <a
                  href={legalDocumentsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline">
                  {acceptTermsAndConditionsMessages.riskDisclosuresLink}
                </a>
                &nbsp;{acceptTermsAndConditionsMessages.and}&nbsp;
                <a
                  href={legalDocumentsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline">
                  {acceptTermsAndConditionsMessages.disclaimersLink}
                </a>
                .
              </label>
            </div>
          </div>

          {showBundleRebalanceCheckbox && (
            <div className="flex items-start py-4">
              <div className="flex items-center flex-shrink-0">
                <input
                  data-browsing-ignore
                  id="bundleRebalance"
                  type="checkbox"
                  className="form-checkbox h-6 w-6 text-indigo-600 transition duration-150 ease-in-out rounded-md"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setState(prevState => ({
                      ...prevState,
                      bundleRebalance: event.target.checked,
                    }));
                  }}
                />
              </div>
              <div className="ml-4 text-sm leading-5">
                <label
                  htmlFor="bundleRebalance"
                  className="text-gray-700 font-light">
                  {
                    acceptTermsAndConditionsMessages.acceptedBundleRebalanceLabel
                  }
                  &nbsp;
                  <a
                    href={legalDocumentsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline">
                    {acceptTermsAndConditionsMessages.bundleRebalanceLink + ' '}
                  </a>
                  and&nbsp;
                  <a
                    href={legalDocumentsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline">
                    {' ' + acceptTermsAndConditionsMessages.discretionaryTerms}
                  </a>
                  .
                </label>
              </div>
            </div>
          )}

          <div className="flex items-start py-4">
            <div className="flex items-center flex-shrink-0">
              <input
                data-browsing-ignore
                id="acceptCurrencies"
                type="checkbox"
                className="form-checkbox h-6 w-6 text-indigo-600 transition duration-150 ease-in-out rounded-md"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setState(prevState => ({
                    ...prevState,
                    supportedCurrencies: event.target.checked,
                  }));
                }}
              />
            </div>
            <div className="ml-4 text-sm leading-5">
              <label
                htmlFor="acceptCurrencies"
                className="text-gray-700 font-light">
                {acceptTermsAndConditionsMessages.supportedCurrenciesLabel}
                &nbsp;
                <a
                  href={supportedCurrenciesUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline">
                  {acceptTermsAndConditionsMessages.supportedCurrenciesLink}
                </a>
                .
              </label>
            </div>
          </div>

          <div className="flex items-start py-4">
            <div className="flex items-center flex-shrink-0">
              <input
                data-browsing-ignore
                id="taxLiabilityNotInRestrictedCountry"
                type="checkbox"
                className="form-checkbox h-6 w-6 text-indigo-600 transition duration-150 ease-in-out rounded-md"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setState(prevState => ({
                    ...prevState,
                    taxLiabilityNotInRestrictedCountry: event.target.checked,
                  }));
                }}
              />
            </div>
            <div className="ml-4 text-sm leading-5">
              <label
                htmlFor="taxLiabilityNotInRestrictedCountry"
                className="text-gray-700 font-light">
                {
                  acceptTermsAndConditionsMessages.taxLiabilityNotInRestrictedCountryLabel
                }
              </label>
            </div>
          </div>
          {isIFACustomer ? (
            <div className="flex items-start py-4">
              <div className="flex items-center flex-shrink-0">
                <input
                  data-browsing-ignore
                  id="customerIFAAccount"
                  type="checkbox"
                  className="form-checkbox h-6 w-6 text-indigo-600 transition duration-150 ease-in-out rounded-md"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setState(prevState => ({
                      ...prevState,
                      hasIFAAccount: event.target.checked,
                    }));
                  }}
                />
              </div>
              <div className="ml-7 text-sm leading-5">
                <label
                  htmlFor="customerIFAAccount"
                  className="text-gray-700 font-light">
                  {
                    acceptTermsAndConditionsMessages.customerIFAAccountCheckboxLabel
                  }
                </label>
              </div>
            </div>
          ) : null}

          <div className="flex items-start py-4">
            <div className="flex items-center flex-shrink-0">
              <input
                data-browsing-ignore
                id="acceptNewsletter"
                type="checkbox"
                className="form-checkbox h-6 w-6 text-indigo-600 transition duration-150 ease-in-out rounded-md"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setState(prevState => ({
                    ...prevState,
                    acceptNewsletter: event.target.checked,
                  }));
                }}
              />
            </div>
            <div className="ml-4 text-sm leading-5">
              <label
                htmlFor="acceptNewsletter"
                className="text-gray-700 font-light">
                {acceptTermsAndConditionsMessages.acceptNewsletterLabel}
              </label>
            </div>
          </div>

          <div className="mt-6">
            <span className="block w-full rounded-md">
              <Button
                loading={acceptTermsResult?.isLoading}
                onClick={onContinuePress}
                disabled={!isStateValid()}
                w-full
                flex
                justify-center
                py-2
                px-4
                border
                border-transparent
                text-sm
                font-normal
                rounded-md
                text-white
                transition
                duration-150
                ease-in-out
                bg-vivid-blue
                type="submit">
                {acceptTermsAndConditionsMessages.acceptAndContinueButton}
              </Button>
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
